import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { fixedValues } from "../../utils/fixedValues";
import {
  organisationMemberAdded,
  organisationMemberRemoved,
} from "../../utils/analytics";
import {
  fetchOrganisationalMembers as fetchOrganisationalMembersApi,
  getUsers,
  inviteOrganisationAdmin,
  inviteUser as inviteTournamentUser,
  searchUsers as searchUsersApi,
  deleteMember as deleteMemberApi,
  bulkDeleteMembers as bulkDeleteMembersApi,
  bulkDeleteTeams as bulkDeleteTeamsApi,
  inviteMember as inviteMemberApi,
  acceptMemberInvitation as acceptMemberInvitationApi,
} from "../actions/usersActions";

const initialState = {
  user: {},
  users: [],
  organisationalMembers: [],
  loading: false,
  error: null,
  success: false,
  selectedUser: null,
  selectedRole: null,
  searchResults: [],
  isSearching: false,
  searchError: null,
};

export const fetchAllUsers = createAsyncThunk(
  "user/fetchAllUsers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getUsers(params);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchUsers = createAsyncThunk(
  "user/searchUsers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await searchUsersApi(params);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const inviteUser = createAsyncThunk(
  "user/inviteUser",
  async (
    { userId, email, fullName, role, organisationId, tournamentId, categoryId },
    { rejectWithValue, getState }
  ) => {
    try {
      let response;
      // Build payload - either userId OR email+fullName
      const payload = { role };
      if (userId) {
        payload.userId = userId;
      } else if (email) {
        payload.email = email;
        if (fullName) payload.fullName = fullName;
      }

      if (role === fixedValues.userRoles.ORGANISATION_ADMIN) {
        response = await inviteOrganisationAdmin(organisationId, payload);
      } else if (
        role === fixedValues.userRoles.TOURNAMENT_ADMIN ||
        role === fixedValues.userRoles.DIVISION_ADMIN ||
        role === fixedValues.userRoles.REFEREE
      ) {
        response = await inviteTournamentUser(
          tournamentId,
          categoryId,
          payload
        );
      } else {
        throw new Error("Invalid role selected");
      }
      organisationMemberAdded({
        organisationId,
        invitedUserId: userId,
        invitedEmail: email,
        role,
        user: getState()?.user?.user,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchOrganisationalMembers = createAsyncThunk(
  "user/fetchOrganisationalMembers",
  async (
    { organisationId, tournamentId, categoryId },
    { rejectWithValue }
  ) => {
    try {
      let response;
      response = await fetchOrganisationalMembersApi(organisationId, {
        tournamentId,
        categoryId,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Invite a member (referee or admin) at tournament level
export const inviteMember = createAsyncThunk(
  "user/inviteMember",
  async ({ tournamentId, payload }, { rejectWithValue }) => {
    try {
      const response = await inviteMemberApi(tournamentId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Accept member invitation (auth required)
export const acceptMemberInvitation = createAsyncThunk(
  "user/acceptMemberInvitation",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await acceptMemberInvitationApi(token);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Delete single member (referee or admin)
export const deleteMember = createAsyncThunk(
  "user/deleteMember",
  async ({ tournamentId, memberId }, { rejectWithValue, getState }) => {
    try {
      await deleteMemberApi(tournamentId, memberId);
      organisationMemberRemoved({
        tournamentId,
        memberId,
        user: getState()?.user?.user,
      });
      return { memberId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Bulk delete members (referees or admins)
export const bulkDeleteMembers = createAsyncThunk(
  "user/bulkDeleteMembers",
  async ({ tournamentId, memberIds }, { rejectWithValue }) => {
    try {
      await bulkDeleteMembersApi(tournamentId, memberIds);
      return { memberIds };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Bulk delete teams
export const bulkDeleteTeams = createAsyncThunk(
  "user/bulkDeleteTeams",
  async ({ tournamentId, categoryId, teamIds }, { rejectWithValue }) => {
    try {
      await bulkDeleteTeamsApi(tournamentId, categoryId, teamIds);
      return { teamIds };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    updateUserInfo: (state, action) => {
      const data = action.payload;
      state.user = data.userData;
    },

    clearUser: (state) => {
      state.user = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(inviteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.organisationalMembers = [
          ...state.organisationalMembers,
          action.payload,
        ];
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrganisationalMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchOrganisationalMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.organisationalMembers = action.payload;
      })
      .addCase(fetchOrganisationalMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const getUser = (state) => state.user.user;
export const getTeam = (state) => state.user.team
export const {
  updateUserInfo,
  clearUser,
  setSelectedUser,
  setSelectedRole,
  resetInviteState,
} = usersSlice.actions;

export default usersSlice.reducer;
